import React from 'react';
import { FileCheck, CreditCard, Gift, Ban, AlertTriangle, Scale, MessageSquare, MessageCircle, Sparkles } from 'lucide-react';

const TermsOfServicePage: React.FC = () => {
  const lastUpdate = '13 de agosto de 2026';

  const sections = [
    {
      title: '1. Objeto y Partes',
      Icon: FileCheck,
      body: (
        <p>
          Las presentes Condiciones Generales regulan el acceso y uso de la plataforma SoArtistic y sus
          servicios asociados (web y aplicación móvil) entre SoArtistic (“el Prestador”) y el usuario o
          usuaria registrado/a (“el Usuario”). El uso de la plataforma implica la aceptación sin reservas
          de todas las cláusulas.
        </p>
      ),
    },
    {
      title: '2. Registro y Cuenta',
      Icon: UserShieldIcon,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>El Usuario declara ser mayor de edad (18 años) o disponer del consentimiento de sus tutores.</li>
          <li>Acceso mínimo 16 años con verificación de edad (Didit) para contenido catalogado como adulto.</li>
          <li>Responsabilidad por la confidencialidad de credenciales y notificación de accesos no autorizados.</li>
          <li>Prohibición de ceder, vender o licenciar la cuenta a terceros.</li>
        </ul>
      ),
    },
    {
      title: '3. Servicios Principales',
      Icon: Sparkles,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Marketplace de obras originales, impresiones y coleccionables artísticos.</li>
          <li>Suscripción Premium para artistas y visitantes (planes mensuales / anuales).</li>
          <li>Sistema de publicidad contextual gestionada con presupuestos del anunciante.</li>
          <li>Colaboraciones, eventos y mensajería directa entre usuarios registrados.</li>
          <li>Verificación de edad y moderación centralizada según la política propia de SoArtistic.</li>
        </ul>
      ),
    },
    {
      title: '4. Precios, Pagos y Facturación',
      Icon: CreditCard,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Los precios mostrados incluyen IVA y tasas aplicables en el momento de la compra.</li>
          <li>Pagos procesados exclusivamente por Stripe; SoArtistic no almacena tarjetas.</li>
          <li>Facturas emitidas mediante Holded con CIF/NIF declarado en Payment Settings.</li>
          <li>Obras vendidas bajo comisión por SoArtistic según plan del artista (Premium/Pro).</li>
          <li>Devolución: derecho de desistimiento 14 días salvo bienes personalizados o descargables.</li>
        </ul>
      ),
    },
    {
      title: '5. Propiedad Intelectual',
      Icon: Gift,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>El artista conserva íntegramente los derechos de autor de su obra.</li>
          <li>Con la subida, el Usuario autoriza a SoArtistic a reproducir y distribuir la obra como parte del marketplace.</li>
          <li>Licencia no exclusiva, limitada al plazo de la suscripción / venta y retirada inmediata bajo petición.</li>
          <li>Denuncias de infracción a legal@soartistic.es con pruebas y datos de contacto.</li>
        </ul>
      ),
    },
    {
      title: '6. Conducta Prohibida',
      Icon: Ban,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Contenido obsceno, violento, discriminatorio, xenófobo, apología del delito o contrario a la moral.</li>
          <li>Uso no autorizado de marcas, obras protegidas o imágenes de terceros sin consentimiento.</li>
          <li>Fraude de clics, manipulación de métricas, suplantación de identidad o ataques a la infraestructura.</li>
          <li>Spam, phishing, venta de cuentas o captación con fines comerciales ajenos a SoArtistic.</li>
        </ul>
      ),
    },
    {
      title: '7. Moderación y Suspensión',
      Icon: AlertTriangle,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>El equipo de moderación revisa contenido y aplica advertencias, suspensiones o bajas permanentes.</li>
          <li>Flujo: advertencia → suspensión 7 días → suspensión 30 días → cancelación definitiva según gravedad.</li>
          <li>Las decisiones se notifican por email y son apelables en plazo de 10 días hábiles.</li>
          <li>SoArtistic puede retirar contenido ilegal o lesivo sin previo aviso.</li>
        </ul>
      ),
    },
    {
      title: '8. Responsabilidad',
      Icon: Scale,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>El Prestador actúa como mero intermediario y no verifica la calidad, originalidad o autenticidad de la obra.</li>
          <li>Responsabilidad máxima: importe abonado en los últimos 12 meses por el Usuario, excluida responsabilidad objetiva inderogable.</li>
          <li>Disponibilidad “como esté” (best effort); no se garantiza disponibilidad ininterrumpida 24x7.</li>
          <li>Fuerza mayor: actos de terceros, ataques DDoS, fallos de proveedores (Supabase/Stripe).</li>
        </ul>
      ),
    },
    {
      title: '9. Atención al Cliente y Reclamaciones',
      Icon: MessageSquare,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Canal: soporte@soartistic.es con respuesta en 48/72 h hábiles.</li>
          <li>Resolución de conflictos a través de la plataforma de resolución de litigios en línea de la UE: https://ec.europa.eu/consumers/odr</li>
          <li>Reclamación por tarjeta: dentro del plazo legal (13 meses) vía Stripe con documento acreditativo.</li>
        </ul>
      ),
    },
    {
      title: '10. Comunicaciones Comerciales',
      Icon: MessageCircle,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Se solicita consentimiento opt-in para envíos publicitarios.</li>
          <li>Botón “Darse de baja” presente en cada email (LGSSI).</li>
          <li>Canales transaccionales (facturas, avisos de seguridad) no requieren consentimiento.</li>
        </ul>
      ),
    },
    {
      title: '11. Ley Aplicable y Jurisdicción',
      Icon: Scale,
      body: (
        <ul className="list-disc pl-6 space-y-2">
          <li>Ley aplicable: española.</li>
          <li>Fuero: juzgados y tribunales de la ciudad donde resida el consumidor si concurren requisitos de normativa de protección de consumidores, o en su defecto los del domicilio del Prestador.</li>
        </ul>
      ),
    },
  ];

  return (
    <article className="mx-auto max-w-3xl px-6 py-16 text-slate-800">
      <header className="mb-10 border-b pb-8">
        <p className="text-sm uppercase tracking-widest text-purple-600 mb-2">Anexo D.3</p>
        <h1 className="text-4xl font-black tracking-tight mb-3">Condiciones Generales de SoArtistic</h1>
        <p className="text-slate-500">Última actualización: {lastUpdate}.</p>
        <p className="mt-3 text-sm">
          Cumplimiento del Reglamento General para la Protección de Datos (RGPD), la Ley de Servicios de la
          Sociedad de la Información (LSSI-CE), la Ley de Garantías y Uso Racional de los Servicios de la
          Energía y normativa específica de marketplace (RD 7/2021) y protección al consumidor.
        </p>
      </header>

      <div className="space-y-10">
        {sections.map(({ title, Icon, body }) => (
          <section key={title} className="scroll-mt-24" id={slugify(title)}>
            <div className="flex items-start gap-3 mb-3">
              <div className="mt-1 rounded-lg bg-emerald-50 p-2 text-emerald-700">
                <Icon size={18} />
              </div>
              <h2 className="text-xl font-bold">{title}</h2>
            </div>
            <div className="text-sm leading-relaxed text-slate-700 pl-12">{body}</div>
          </section>
        ))}
      </div>

      <footer className="mt-16 rounded-xl border bg-slate-50 p-6 text-xs text-slate-500">
        Documento generado para publicar en la web y alojar el enlace público requerido por Google Play
        Data Safety y App Store Product Page. URL pública: <a className="underline" href="/terms">/terms</a>.
      </footer>
    </article>
  );
};

function UserShieldIcon(props: { size?: number }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width={props.size ?? 24} height={props.size ?? 24} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10" /><circle cx="12" cy="11" r="3" />
    </svg>
  );
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

export default TermsOfServicePage;
